// What an agent says of its question as it asks it, as the session pane's
// card shows it (variant A, Poulpe-404): whether it is urgent, whether the
// agent waits for the answer, when it stops waiting and what it goes on
// with then, and when it was last revised. Once that expiry has passed the
// agent waits no more, and the card never says it does; the question stays
// open, and an answer still reaches the agent.
import { useEffect, useReducer } from 'react'
import type { Expiry, Question } from '../api/types'
import { clockTime, longAge } from '../components/Time'

/** Whether the question's expiry has passed at `now`: from its very moment on, as the server reads it. */
export function expired(question: Pick<Question, 'expiry'>, now: number): boolean {
  return !!question.expiry && now >= Date.parse(question.expiry.at)
}

/** What an expired question says of its expiry, at `now`: when its agent stopped waiting, and what it went on with. */
export function expiredText(expiry: Expiry, now: number): string {
  return `Expired · it stopped waiting at ${clockTime(expiry.at, now)} and went on with “${expiry.instead}”`
}

/** Whether its agent still waits for the answer at `now`: it said it would, and its expiry has not passed. */
export function waits(question: Pick<Question, 'blocking' | 'expiry'>, now: number): boolean {
  return question.blocking && !expired(question, now)
}

/** Questions in the order the dock shows them at `now`: urgent first, then those the agent waits on, each group in the order given. */
export function byNeed<Q extends Pick<Question, 'urgent' | 'blocking' | 'expiry'>>(questions: readonly Q[], now: number): Q[] {
  const rank = (question: Q) => (question.urgent ? 0 : 2) + (waits(question, now) ? 0 : 1)
  return [...questions].sort((a, b) => rank(a) - rank(b))
}

/** Now, read at each render, and a render again when the next of these questions' expiries passes. */
export function useExpiryClock(questions: readonly Pick<Question, 'expiry'>[]): number {
  const [, tick] = useReducer((n: number) => n + 1, 0)
  const now = Date.now()
  const next = Math.min(...questions.map((question) => (question.expiry ? Date.parse(question.expiry.at) : Infinity)).filter((at) => at > now))
  useEffect(() => {
    if (!Number.isFinite(next)) return
    const timer = setTimeout(tick, Math.max(0, next - Date.now()))
    return () => clearTimeout(timer)
  }, [next])
  return now
}

/** "Urgent" and "The agent waits", each when it holds at `now`, each with a space after it for a screen reader; nothing otherwise. */
export function QuestionFlags({ question, now }: { question: Pick<Question, 'urgent' | 'blocking' | 'expiry'>; now: number }) {
  const until = question.expiry
  return (
    <>
      {question.urgent && (
        <>
          <span className="asks-flag is-urgent">Urgent</span>{' '}
        </>
      )}
      {waits(question, now) && (
        <>
          <span className="asks-flag is-waiting" title={until ? `Until ${clockTime(until.at, now)}, then it goes on with “${until.instead}”` : undefined}>
            The agent waits
          </span>{' '}
        </>
      )}
    </>
  )
}

/** "revised <age>" at `now`, whose hover says an answer to an earlier version is refused; nothing before its first revision. `first` when it starts a line. */
export function QuestionRevised({ question, now, first = false }: { question: Pick<Question, 'revised_at'>; now: number; first?: boolean }) {
  if (!question.revised_at) return null
  return (
    <span className="asks-revised" title="An answer to an earlier version is refused.">
      {first ? 'Revised' : 'revised'} {longAge(question.revised_at, now)}
    </span>
  )
}
